import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FigmaFileLoader from './components/FigmaFileLoader';
import FigmaCanvas from './components/FigmaCanvas';
import ComponentTreeSidebar from './components/ComponentTreeSidebar';
import AnnotationPanel from './components/AnnotationPanel';
import AnnotationForm from './components/AnnotationForm';
import MarkdownExportDialog from './components/MarkdownExportDialog';
import ToolBar from './components/ToolBar';
import TopNavBar from './components/TopNavBar';
import { useFigmaApi } from './hooks/useFigmaApi';
import { useAnnotations } from './hooks/useAnnotations';
import { useCanvasInteraction } from './hooks/useCanvasInteraction';
import { annotationApi } from './api/annotationApi';
import { flattenNodes, findNodeAtPosition } from './utils/nodeFlattener';
import { FigmaAnnotationRequest, FigmaAnnotationResponse, FigmaFileResponse } from './types/annotation';
import { FigmaFrame, FlattenedNode } from './types/figma';

type ViewState = 'loader' | 'frame-selector' | 'canvas';

const slugifyName = (value: string) => {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/-+/g, '-')
    .replace(/\s/g, '-');
};

const FigmaAnnotatorPage: React.FC = () => {
  const navigate = useNavigate();
  const { slug, frameId: frameIdParam } = useParams();
  const slugParam = slug ? slugifyName(slug) : null;
  const frameIdParamNormalized = frameIdParam ? decodeURIComponent(frameIdParam).replace(/-/g, ':') : null;

  // Core state
  const [viewState, setViewState] = useState<ViewState>('loader');
  const [fileKey, setFileKey] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [previousFiles, setPreviousFiles] = useState<FigmaFileResponse[]>([]);
  const [selectedFrame, setSelectedFrame] = useState<FigmaFrame | null>(null);
  const [mode, setMode] = useState<'view' | 'annotate'>('annotate');

  // Canvas state
  const [imageUrl, setImageUrl] = useState('');
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [flatNodes, setFlatNodes] = useState<FlattenedNode[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [frameBBox, setFrameBBox] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [nodesLoading, setNodesLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const hoverDebounceRef = useRef<number | null>(null);
  const lastHoverNodeIdRef = useRef<string | null>(null);
  const imageRequestRef = useRef<string | null>(null);
  const selectedFrameRef = useRef<FigmaFrame | null>(null);
  const [framePickerMode, setFramePickerMode] = useState<'none' | 'next-frame'>('none');
  const [pickedNextFrameId, setPickedNextFrameId] = useState<string | null>(null);
  const [carouselExpanded, setCarouselExpanded] = useState(false);
  const [frameSearch, setFrameSearch] = useState('');

  // Annotation form state
  const [showForm, setShowForm] = useState(false);
  const [editingAnnotation, setEditingAnnotation] = useState<FigmaAnnotationResponse | null>(null);
  const [pendingPin, setPendingPin] = useState<{ x: number; y: number } | null>(null);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<number | null>(null);

  // Export state
  const [showExport, setShowExport] = useState(false);

  // Hooks
  const figma = useFigmaApi();
  const annotationsHook = useAnnotations();
  const canvas = useCanvasInteraction();

  const clearHoverDebounce = useCallback(() => {
    if (hoverDebounceRef.current !== null) {
      window.clearTimeout(hoverDebounceRef.current);
      hoverDebounceRef.current = null;
    }
    lastHoverNodeIdRef.current = null;
  }, []);

  const handleRequestNextFramePick = useCallback(() => {
    setFramePickerMode(prev => {
      const next = prev === 'next-frame' ? 'none' : 'next-frame';
      setCarouselExpanded(next === 'next-frame');
      return next;
    });
  }, []);

  const handlePickedNextFrameConsumed = useCallback(() => {
    setPickedNextFrameId(null);
  }, []);

  // Load previous files on mount
  useEffect(() => {
    annotationApi.getAllFiles().then(setPreviousFiles).catch(() => {});
  }, []);

  // Load file structure
  const handleLoadFile = useCallback(async (key: string) => {
    setViewState('loader');
    setFileKey(null);
    setFileName('');
    setSelectedFrame(null);
    selectedFrameRef.current = null;
    setFrameBBox(null);
    setFlatNodes([]);
    setSelectedNodeId(null);
    setHoveredNodeId(null);
    setSelectedAnnotationId(null);
    setEditingAnnotation(null);
    setPendingPin(null);
    setShowForm(false);
    setFramePickerMode('none');
    setCarouselExpanded(false);
    setFrameSearch('');
    setImageUrl('');
    setImageWidth(0);
    setImageHeight(0);
    setImageLoading(false);
    setNodesLoading(false);
    imageRequestRef.current = null;
    annotationsHook.clearAnnotations();

    const fileData = await figma.loadFile(key);
    if (!fileData) return;

    const name = fileData.name || '';
    const nextSlugBase = slugifyName(name);
    const nextSlug = nextSlugBase || key;

    setFileKey(key);
    setFileName(fileData.name);

    if (nextSlug && slugParam !== nextSlug) {
      if (frameIdParamNormalized) {
        navigate(`/figma-annotator/${nextSlug}/frame/${encodeURIComponent(frameIdParamNormalized)}`);
      } else {
        navigate(`/figma-annotator/${nextSlug}`);
      }
    }

    try {
      localStorage.setItem('figma-annotator:last-file-key', key);
      localStorage.setItem('figma-annotator:last-file-name', name);
      const raw = localStorage.getItem('figma-annotator:slug-map');
      const map = raw ? JSON.parse(raw) : {};
      map[nextSlug] = key;
      localStorage.setItem('figma-annotator:slug-map', JSON.stringify(map));
    } catch {
      // ignore storage failures
    }

    // Register/upsert file in backend
    await annotationApi.createOrUpdateFile({
      fileKey: key,
      fileName: fileData.name,
      lastModified: fileData.lastModified,
      thumbnailUrl: fileData.thumbnailUrl,
    });

    // Refresh previous files list
    annotationApi.getAllFiles().then(setPreviousFiles).catch(() => {});

    setViewState('frame-selector');

    // Load thumbnails for all frames (after switching to frame-selector view)
    const filePages = fileData.document?.children || [];
    const allFrames: FigmaFrame[] = [];
    for (const page of filePages) {
      for (const child of page.children || []) {
        if (child.type === 'FRAME' || child.type === 'COMPONENT' || child.type === 'COMPONENT_SET') {
          allFrames.push(child as FigmaFrame);
        }
      }
    }
    figma.loadFrameThumbnails(key, allFrames);
  }, [figma, slugParam, navigate, annotationsHook, frameIdParamNormalized]);

  // Auto-load file based on URL slug
  useEffect(() => {
    if (figma.loading) return;

    let targetKey: string | null = null;

    if (slugParam) {
      const match = previousFiles.find(f => slugifyName(f.fileName) === slugParam);
      if (match) {
        targetKey = match.fileKey;
      } else {
        try {
          const raw = localStorage.getItem('figma-annotator:slug-map');
          const map = raw ? JSON.parse(raw) : {};
          if (map && map[slugParam]) {
            targetKey = map[slugParam];
          }
        } catch {
          // ignore storage failures
        }
      }
    } else {
      return;
    }

    if (targetKey && targetKey !== fileKey) {
      handleLoadFile(targetKey);
    }
  }, [slugParam, previousFiles, fileKey, figma.loading, handleLoadFile]);

  const handleSelectPreviousFile = useCallback(async (file: FigmaFileResponse) => {
    await handleLoadFile(file.fileKey);
  }, [handleLoadFile]);

  const handleDeleteFile = useCallback(async (fileId: number) => {
    await annotationApi.deleteFile(fileId);
    setPreviousFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  // Select a frame — matches POC pattern:
  // 1. Fetch node tree for hit detection
  // 2. Fetch image URL from Figma (S3 URL), use directly as img src
  // 3. Load annotations
  const handleSelectFrame = useCallback(async (frame: FigmaFrame, force = false) => {
    if (!fileKey) return;
    if (!force && selectedFrameRef.current?.id === frame.id) return;
    imageRequestRef.current = frame.id;
    setFramePickerMode('none');
    setCarouselExpanded(false);
    setSelectedFrame(frame);
    selectedFrameRef.current = frame;
    setViewState('canvas');
    canvas.resetZoom();
    setSelectedNodeId(null);
    setHoveredNodeId(null);
    setSelectedAnnotationId(null);
    clearHoverDebounce();
    setImageUrl('');
    setImageWidth(0);
    setImageHeight(0);
    setImageLoading(true);

    // Update URL immediately before async work to prevent URL-sync useEffect from reverting
    const encoded = encodeURIComponent(frame.id);
    if (slugParam) {
      if (frameIdParamNormalized !== frame.id) {
        navigate(`/figma-annotator/${slugParam}/frame/${encoded}`);
      }
    } else if (frameIdParamNormalized !== frame.id) {
      navigate(`/figma-annotator/frame/${encoded}`);
    }

    // Load node tree for hit detection
    setNodesLoading(true);
    try {
      const nodesData = await figma.loadFrameNodes(fileKey, frame.id);
      const nodeMap = nodesData?.nodes || {};
      const rootNode = Object.values(nodeMap)[0] as any;
      if (rootNode?.document) {
        const docNode = rootNode.document;
        const flattened = flattenNodes([docNode]);
        setFlatNodes(flattened);
        setFrameBBox(docNode.absoluteBoundingBox || frame.absoluteBoundingBox || null);
      } else {
        setFrameBBox(frame.absoluteBoundingBox || null);
      }
    } catch {
      setFlatNodes([]);
      setFrameBBox(frame.absoluteBoundingBox || null);
    } finally {
      setNodesLoading(false);
    }

    // Fetch rendered image URL from Figma API (returns S3 URL, used directly)
    const url = await figma.loadFrameImageUrl(fileKey, frame.id);
    if (imageRequestRef.current !== frame.id) return;
    if (url) {
      setImageUrl(url);
      // Preload image to get dimensions
      const img = new Image();
      img.onload = () => {
        if (imageRequestRef.current !== frame.id) return;
        setImageWidth(img.naturalWidth);
        setImageHeight(img.naturalHeight);
        setImageLoading(false);
      };
      img.onerror = () => {
        if (imageRequestRef.current !== frame.id) return;
        setImageLoading(false);
      };
      img.src = url;
    } else {
      setImageLoading(false);
    }

    // Load annotations for this frame
    await annotationsHook.loadAnnotations(fileKey, frame.id);
  }, [fileKey, figma, annotationsHook, canvas, clearHoverDebounce, navigate, slugParam, frameIdParamNormalized]);

  useEffect(() => {
    if (!fileKey || !frameIdParamNormalized) return;
    if (selectedFrame?.id === frameIdParamNormalized) return;

    const frame = figma.frames.find(item => item.id === frameIdParamNormalized);
    if (frame) {
      handleSelectFrame(frame, true);
      return;
    }

    handleSelectFrame({ id: frameIdParamNormalized, name: frameIdParamNormalized, type: 'FRAME' }, true);
  }, [fileKey, frameIdParamNormalized, figma.frames, selectedFrame, handleSelectFrame]);

  const handleOpenFrameById = useCallback(
    (frameId: string) => {
      const frame = figma.frames.find(item => item.id === frameId);
      if (frame) {
        handleSelectFrame(frame, true);
        return;
      }
      handleSelectFrame({ id: frameId, name: frameId, type: 'FRAME' }, true);
    },
    [figma.frames, handleSelectFrame]
  );

  // Handle canvas click (place a pin)
  const handleCanvasClick = useCallback((xPercent: number, yPercent: number) => {
    setPendingPin({ x: xPercent, y: yPercent });
    setEditingAnnotation(null);
    setShowForm(true);

    if (frameBBox && flatNodes.length > 0) {
      const figmaX = frameBBox.x + (xPercent / 100) * frameBBox.width;
      const figmaY = frameBBox.y + (yPercent / 100) * frameBBox.height;
      const node = findNodeAtPosition(flatNodes, figmaX, figmaY);
      setSelectedNodeId(node ? node.id : null);
    }
  }, [frameBBox, flatNodes]);

  // Handle annotation selection
  const handleAnnotationClick = useCallback((annotation: FigmaAnnotationResponse) => {
    setSelectedAnnotationId(annotation.id);
    if (annotation.nodeId) {
      setSelectedNodeId(annotation.nodeId);
    }
  }, []);

  // Save annotation (create or update)
  const handleSaveAnnotation = useCallback(async (request: FigmaAnnotationRequest) => {
    if (!fileKey) return;

    if (editingAnnotation) {
      await annotationsHook.updateAnnotation(editingAnnotation.id, request);
    } else {
      await annotationsHook.createAnnotation(fileKey, request);
    }

    setShowForm(false);
    setEditingAnnotation(null);
    setPendingPin(null);
    setFramePickerMode('none');
    setCarouselExpanded(false);
  }, [fileKey, editingAnnotation, annotationsHook]);

  const handleEditAnnotation = useCallback((annotation: FigmaAnnotationResponse) => {
    setEditingAnnotation(annotation);
    setShowForm(true);
    if (annotation.nodeId) {
      setSelectedNodeId(annotation.nodeId);
    }
  }, []);

  const handleDeleteAnnotation = useCallback(async (id: number) => {
    await annotationsHook.deleteAnnotation(id);
    if (selectedAnnotationId === id) {
      setSelectedAnnotationId(null);
    }
  }, [annotationsHook, selectedAnnotationId]);

  const handleCancelForm = useCallback(() => {
    setShowForm(false);
    setEditingAnnotation(null);
    setPendingPin(null);
    setFramePickerMode('none');
    setCarouselExpanded(false);
  }, []);

  const handleBack = useCallback(() => {
    if (viewState === 'canvas') {
      setViewState('frame-selector');
      setSelectedFrame(null);
      setFrameBBox(null);
      setSelectedNodeId(null);
      setHoveredNodeId(null);
      setSelectedAnnotationId(null);
      setNodesLoading(false);
      setImageLoading(false);
      imageRequestRef.current = null;
      setFramePickerMode('none');
      setCarouselExpanded(false);
      clearHoverDebounce();
    } else {
      setViewState('loader');
      setFileKey(null);
      setFrameBBox(null);
      setSelectedNodeId(null);
      setHoveredNodeId(null);
      setSelectedAnnotationId(null);
      setNodesLoading(false);
      setImageLoading(false);
      imageRequestRef.current = null;
      setFramePickerMode('none');
      setCarouselExpanded(false);
      clearHoverDebounce();
    }
  }, [viewState, clearHoverDebounce]);

  const handleNodeSelect = useCallback((node: FlattenedNode) => {
    setSelectedNodeId(node.id);
  }, []);

  const scheduleHoverUpdate = useCallback(
    (nodeId: string | null) => {
      if (hoverDebounceRef.current !== null) {
        window.clearTimeout(hoverDebounceRef.current);
      }
      hoverDebounceRef.current = window.setTimeout(() => {
        if (lastHoverNodeIdRef.current !== nodeId) {
          lastHoverNodeIdRef.current = nodeId;
          setHoveredNodeId(nodeId);
        }
      }, 50);
    },
    []
  );

  const handleCanvasHover = useCallback(
    (x: number, y: number) => {
      if (mode !== 'annotate' || !frameBBox || flatNodes.length === 0) return;
      const scaleX = frameBBox.width > 0 ? imageWidth / frameBBox.width : 1;
      const scaleY = frameBBox.height > 0 ? imageHeight / frameBBox.height : 1;
      const figmaX = frameBBox.x + x / (scaleX || 1);
      const figmaY = frameBBox.y + y / (scaleY || 1);
      const node = findNodeAtPosition(flatNodes, figmaX, figmaY);
      scheduleHoverUpdate(node?.id || null);
    },
    [mode, frameBBox, flatNodes, imageWidth, imageHeight, scheduleHoverUpdate]
  );

  const handleCanvasLeave = useCallback(() => {
    if (mode === 'annotate') {
      clearHoverDebounce();
      setHoveredNodeId(null);
    }
  }, [mode, clearHoverDebounce]);

  useEffect(() => {
    return () => {
      clearHoverDebounce();
    };
  }, [clearHoverDebounce]);

  useEffect(() => {
    if (!showForm && framePickerMode !== 'none') {
      setFramePickerMode('none');
      setCarouselExpanded(false);
    }
  }, [showForm, framePickerMode]);

  useEffect(() => {
    if (mode !== 'annotate') {
      clearHoverDebounce();
      setHoveredNodeId(null);
    }
  }, [mode, clearHoverDebounce]);

  const highlightRect = useMemo(() => {
    const highlightNodeId = hoveredNodeId || selectedNodeId;
    if (!highlightNodeId || !frameBBox) return null;
    const node = flatNodes.find(n => n.id === highlightNodeId);
    const bbox = node?.absoluteBoundingBox;
    if (!bbox || frameBBox.width === 0 || frameBBox.height === 0) return null;
    return {
      left: ((bbox.x - frameBBox.x) / frameBBox.width) * 100,
      top: ((bbox.y - frameBBox.y) / frameBBox.height) * 100,
      width: (bbox.width / frameBBox.width) * 100,
      height: (bbox.height / frameBBox.height) * 100,
    };
  }, [flatNodes, frameBBox, hoveredNodeId, selectedNodeId]);

  const frameNameById = useMemo(() => {
    const map: Record<string, string> = {};
    figma.frames.forEach(frame => {
      map[frame.id] = frame.name;
    });
    return map;
  }, [figma.frames]);

  const filteredFrames = useMemo(() => {
    if (!frameSearch.trim()) return figma.frames;
    const lower = frameSearch.toLowerCase();
    return figma.frames.filter(frame => frame.name.toLowerCase().includes(lower) || frame.id.toLowerCase().includes(lower));
  }, [figma.frames, frameSearch]);

  const nodeById = useMemo(() => {
    const map = new Map<string, FlattenedNode>();
    flatNodes.forEach(node => {
      if (node.absoluteBoundingBox) {
        map.set(node.id, node);
      }
    });
    return map;
  }, [flatNodes]);

  const getAnnotationPinPosition = useCallback(
    (annotation: FigmaAnnotationResponse) => {
      if (!imageWidth || !imageHeight) return null;

      if (frameBBox && annotation.nodeId) {
        const node = nodeById.get(annotation.nodeId);
        const bbox = node?.absoluteBoundingBox;
        if (bbox && frameBBox.width > 0 && frameBBox.height > 0) {
          const centerX = bbox.x + bbox.width / 2;
          const centerY = bbox.y + bbox.height / 2;
          const xPercent = ((centerX - frameBBox.x) / frameBBox.width) * 100;
          const yPercent = ((centerY - frameBBox.y) / frameBBox.height) * 100;
          return {
            x: (xPercent / 100) * imageWidth,
            y: (yPercent / 100) * imageHeight,
          };
        }
      }

      if (annotation.pinXPercent != null && annotation.pinYPercent != null) {
        return {
          x: (annotation.pinXPercent / 100) * imageWidth,
          y: (annotation.pinYPercent / 100) * imageHeight,
        };
      }

      return null;
    },
    [frameBBox, nodeById, imageWidth, imageHeight]
  );

  // --- Render ---

  if (viewState === 'loader') {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' }}>
        <TopNavBar title="Figma Annotator" />
        <FigmaFileLoader
          previousFiles={previousFiles}
          loading={figma.loading}
          onLoadFile={handleLoadFile}
          onSelectPreviousFile={handleSelectPreviousFile}
          onDeleteFile={handleDeleteFile}
        />
        {figma.error && (
          <div style={{ padding: '12px 20px', color: '#e74c3c', fontSize: '14px' }}>
            {figma.error}
          </div>
        )}
      </div>
    );
  }

  if (viewState === 'frame-selector') {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' }}>
        <TopNavBar title="Figma Annotator" subtitle={fileName} onBack={handleBack} />
        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {figma.frames.length === 0 ? (
            <div style={{ color: '#888', textAlign: 'center', padding: '40px' }}>
              No frames found in this file.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
              {figma.frames.map(frame => (
                <div
                  key={frame.id}
                  onClick={() => handleSelectFrame(frame)}
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '12px',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.2s',
                    background: '#fff',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)')}
                  onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
                >
                  <div
                    style={{
                      height: '120px',
                      background: '#f0f0f0',
                      borderRadius: '4px',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    {figma.frameThumbnails[frame.id] ? (
                      <img
                        src={figma.frameThumbnails[frame.id]}
                        alt={frame.name}
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        onError={e => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <span style={{ color: '#aaa', fontSize: '12px' }}>Loading...</span>
                    )}
                  </div>
                  <div style={{ fontWeight: 500, fontSize: '14px' }}>{frame.name}</div>
                  <div style={{ fontSize: '12px', color: '#888' }}>{frame.type}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Canvas view
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' }}>
      <TopNavBar title="Figma Annotator" subtitle={`${fileName} / ${selectedFrame?.name || ''}`} onBack={handleBack} />
      <ToolBar
        mode={mode}
        onModeChange={setMode}
        scale={canvas.canvasState.scale}
        onZoomIn={canvas.zoomIn}
        onZoomOut={canvas.zoomOut}
        onResetZoom={canvas.resetZoom}
        onExport={() => setShowExport(true)}
      />

      <div style={{ borderBottom: '1px solid #eee', background: '#fff' }}>
        <div style={{ padding: '8px 12px', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="text"
            value={frameSearch}
            onChange={e => setFrameSearch(e.target.value)}
            placeholder="Search frames..."
            style={{
              width: '260px',
              padding: '6px 8px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '13px',
            }}
          />
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              type="button"
              onClick={() => setCarouselExpanded(false)}
              style={{
                padding: '6px 10px',
                borderRadius: '6px',
                border: carouselExpanded ? '1px solid #cbd5e1' : '2px solid #2563eb',
                background: carouselExpanded ? '#fff' : '#eff6ff',
                color: '#1d4ed8',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              Small
            </button>
            <button
              type="button"
              onClick={() => setCarouselExpanded(true)}
              style={{
                padding: '6px 10px',
                borderRadius: '6px',
                border: carouselExpanded ? '2px solid #2563eb' : '1px solid #cbd5e1',
                background: carouselExpanded ? '#eff6ff' : '#fff',
                color: '#1d4ed8',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              Large
            </button>
          </div>
          {framePickerMode === 'next-frame' && (
            <span style={{ fontSize: '12px', color: '#1d4ed8', fontWeight: 600 }}>
              Pick next frame
            </span>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            gap: '10px',
            padding: '0 12px 10px 12px',
            overflowX: 'auto',
            alignItems: 'center',
            minHeight: framePickerMode === 'next-frame' ? '260px' : carouselExpanded ? '170px' : '110px',
          }}
        >
          {filteredFrames.length === 0 ? (
            <div style={{ color: '#888', fontSize: '13px', padding: '6px 0' }}>No frames found.</div>
          ) : (
            filteredFrames.map(frame => {
              const isActive = selectedFrame?.id === frame.id;
            const thumbWidth =
                framePickerMode === 'next-frame' ? 320 : carouselExpanded ? 200 : 120;
              const thumbHeight =
                framePickerMode === 'next-frame' ? 210 : carouselExpanded ? 130 : 80;
              const itemMinWidth =
                framePickerMode === 'next-frame' ? 340 : carouselExpanded ? 220 : 140;
              return (
                <button
                  type="button"
                  key={frame.id}
                  onClick={() => {
                    if (showForm && framePickerMode === 'next-frame') {
                      setPickedNextFrameId(frame.id);
                      setFramePickerMode('none');
                      setCarouselExpanded(false);
                      return;
                    }
                    handleSelectFrame(frame, true);
                  }}
                  style={{
                    border: isActive ? '2px solid #3b82f6' : '1px solid #ddd',
                    background: '#fff',
                    borderRadius: '8px',
                    padding: '6px',
                    minWidth: itemMinWidth,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div
                    style={{
                      width: thumbWidth,
                      height: thumbHeight,
                      background: '#f1f5f9',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '6px',
                    }}
                  >
                    {figma.frameThumbnails[frame.id] ? (
                      <img
                        src={figma.frameThumbnails[frame.id]}
                        alt={frame.name}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '11px' }}>Loading...</span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? '#1d4ed8' : '#475569',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: thumbWidth,
                    }}
                    title={frame.name}
                  >
                    {frame.name}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left sidebar: Component tree */}
        <ComponentTreeSidebar
          nodes={flatNodes}
          selectedNodeId={selectedNodeId}
          hoveredNodeId={hoveredNodeId}
          loading={nodesLoading}
          onSelectNode={handleNodeSelect}
          onHoverNode={setHoveredNodeId}
        />

        {/* Center: Canvas */}
        <FigmaCanvas
          frameId={selectedFrame?.id || null}
          imageUrl={imageUrl}
          imageWidth={imageWidth}
          imageHeight={imageHeight}
          annotations={annotationsHook.annotations}
          selectedAnnotationId={selectedAnnotationId}
          highlightRect={highlightRect}
          imageLoading={imageLoading}
          getAnnotationPinPosition={getAnnotationPinPosition}
          canvasState={canvas.canvasState}
          mode={mode}
          onCanvasClick={handleCanvasClick}
          onCanvasHover={handleCanvasHover}
          onCanvasLeave={handleCanvasLeave}
          onAnnotationClick={handleAnnotationClick}
          onWheel={canvas.handleWheel}
          onMouseDown={canvas.handleMouseDown}
          onMouseMove={canvas.handleMouseMove}
          onMouseUp={canvas.handleMouseUp}
        />

        {/* Right sidebar: Annotations */}
        <div
          style={{
            width: '320px',
            borderLeft: '1px solid #ddd',
            display: 'flex',
            flexDirection: 'column',
            background: '#fff',
          }}
        >
          <div
            style={{
              padding: '10px 12px',
              borderBottom: '1px solid #eee',
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            Annotations ({annotationsHook.annotations.length})
          </div>

          {showForm ? (
            <div style={{ padding: '12px', overflow: 'auto', flex: 1 }}>
              <AnnotationForm
                frameId={selectedFrame?.id || ''}
                nodeId={selectedNodeId || editingAnnotation?.nodeId || selectedFrame?.id || ''}
                nodeName={
                  flatNodes.find(n => n.id === selectedNodeId)?.name ||
                  editingAnnotation?.nodeName ||
                  selectedFrame?.name
                }
                nodeType={
                  flatNodes.find(n => n.id === selectedNodeId)?.type ||
                  editingAnnotation?.nodeType ||
                  selectedFrame?.type
                }
                pinXPercent={pendingPin?.x ?? editingAnnotation?.pinXPercent}
                pinYPercent={pendingPin?.y ?? editingAnnotation?.pinYPercent}
                editingAnnotation={editingAnnotation}
                frames={figma.frames.map(frame => ({ id: frame.id, name: frame.name }))}
                currentFrameId={selectedFrame?.id || ''}
                pickedNextFrameId={pickedNextFrameId}
                isPickingNextFrame={framePickerMode === 'next-frame'}
                onPickedNextFrameConsumed={handlePickedNextFrameConsumed}
                onRequestNextFramePick={handleRequestNextFramePick}
                onSave={handleSaveAnnotation}
                onCancel={handleCancelForm}
              />
            </div>
          ) : (
            <AnnotationPanel
              annotations={annotationsHook.annotations}
              selectedAnnotationId={selectedAnnotationId}
              onSelectAnnotation={handleAnnotationClick}
              onEditAnnotation={handleEditAnnotation}
              onDeleteAnnotation={handleDeleteAnnotation}
              frameNameById={frameNameById}
              onOpenFrame={handleOpenFrameById}
            />
          )}
        </div>
      </div>

      {showExport && fileKey && (
        <MarkdownExportDialog
          fileKey={fileKey}
          frameId={selectedFrame?.id}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  );
};

export default FigmaAnnotatorPage;
